import styled from 'styled-components';
import { Button } from '../../../components/Button';
import { Camera } from '../../cameras/api/types';
import { ProgressBar } from './ProgressBar';

interface CameraSelectionStepProps {
  heavyStreamingCameras: Camera[];
  selectedCamera: Camera | null;
  onCameraSelect: (camera: Camera) => void;
  onCameraSetup: () => void;
  isSettingUpCamera: boolean;
  setupProgress: number;
}

export const CameraSelectionStep = ({
  heavyStreamingCameras,
  selectedCamera,
  onCameraSelect,
  onCameraSetup,
  isSettingUpCamera,
  setupProgress
}: CameraSelectionStepProps) => {
  const getProgressSubtext = (progress: number) => {
    if (progress < 30) return 'Connecting to camera...';
    if (progress < 60) return 'Configuring livestream settings...';
    if (progress < 90) return 'Setting up livestream access...';
    return 'Finalising configuration...';
  };

  if (heavyStreamingCameras.length === 0) {
    return (
      <NoHeavyCamerasSection>
        <h3>No Heavy Streaming Cameras Found</h3>
        <p>
          You need an active Heavy Streaming plan to use livestreaming. 
          Please upgrade one of your cameras to a Heavy Streaming plan first.
        </p>
        <Button onClick={() => window.location.href = '/dashboard'}>
          Back to Dashboard
        </Button>
      </NoHeavyCamerasSection>
    );
  }

  return (
    <CameraSelectionSection>
      <SectionTitle>Available Heavy Streaming Cameras</SectionTitle>
      <CameraGrid>
        {heavyStreamingCameras.map((camera) => (
          <CameraSelectionCard 
            key={camera.SIM_ICCID}
            onClick={() => onCameraSelect(camera)}
            isSelected={selectedCamera?.SIM_ICCID === camera.SIM_ICCID}
          >
            <CameraName>{camera.nicknameInDatabase}</CameraName>
            <CameraSim>SIM: {camera.SIM_ICCID}</CameraSim>
            <CameraPlan>{camera.stripeSubscription?.productName}</CameraPlan>
          </CameraSelectionCard>
        ))}
      </CameraGrid>
      
      {selectedCamera && (
        <SetupCameraSection>
          <h3>Setup Camera: {selectedCamera.nicknameInDatabase}</h3>
          <p>
            This will configure your camera for livestreaming.
          </p>
          
          {isSettingUpCamera && (
            <ProgressBar
              progress={setupProgress}
              title="Setting up camera router..."
              subtitle={getProgressSubtext(setupProgress)}
            />
          )}
          
          <Button 
            onClick={onCameraSetup}
            isLoading={isSettingUpCamera}
            disabled={isSettingUpCamera}
          >
            {isSettingUpCamera ? 'Setting Up Camera...' : 'Setup Camera for Livestreaming'}
          </Button>
        </SetupCameraSection>
      )}
    </CameraSelectionSection>
  );
};

// Styled Components
const NoHeavyCamerasSection = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.gray}10, ${({ theme }) => theme.colors.gray}20);
  border: 1px solid ${({ theme }) => theme.colors.gray500}30;
  border-radius: 1rem;
  
  h3 {
    color: ${({ theme }) => theme.colors.black};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${({ theme }) => theme.colors.gray900};
    margin-bottom: 2rem;
    line-height: 1.6;
  }
`;

const CameraSelectionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary}20;
`;

const CameraGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const CameraSelectionCard = styled.div<{ isSelected: boolean }>`
  padding: 1.5rem;
  border: 2px solid ${({ theme, isSelected }) => 
    isSelected ? theme.colors.primary : theme.colors.gray500 + '30'};
  border-radius: 0.75rem;
  background: ${({ theme, isSelected }) => 
    isSelected 
      ? `linear-gradient(135deg, ${theme.colors.primary}08, ${theme.colors.primary}15)`
      : theme.colors.white};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CameraName = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 0.5rem;
`;

const CameraSim = styled.p`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.gray900};
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  margin-bottom: 0.5rem;
`;

const CameraPlan = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  margin: 0;
`;

const SetupCameraSection = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.green900}08, ${({ theme }) => theme.colors.green900}15);
  border: 1px solid ${({ theme }) => theme.colors.green900}25;
  border-radius: 0.75rem;
  padding: 2rem;
  text-align: center;
  
  h3 {
    color: ${({ theme }) => theme.colors.black};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${({ theme }) => theme.colors.gray900};
    margin-bottom: 2rem;
    line-height: 1.6;
  }
`;
